// shared/rum.ts — CloudWatch RUM initialization
//
// Initializes AWS CloudWatch Real User Monitoring for:
// - Frontend error tracking (JS exceptions, unhandled rejections)
// - Performance metrics (Web Vitals: LCP, FID, CLS)
// - HTTP request monitoring (status codes, latency)
// - User session tracking (page views, navigation paths)
// - Custom events (API errors with details, page views with normalized paths)
//
// Config comes from environment variables set during build (via CDK pipeline).
// RUM failure never blocks the app — wrapped in try/catch.

import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

let rumClient: AwsRum | null = null;

/**
 * Normalizes a URL path by replacing dynamic segments with generic placeholders.
 * This enables:
 * 1. Aggregation — group all "/applications/:appId" page views together
 * 2. PII protection — strip phone numbers, application IDs from telemetry
 */
export function normalizePagePath(path: string): string {
  return path
    .replace(/\/leads\/[^/]+/, '/leads/:phoneNumber')
    .replace(/\/applications\/create\/[^/]+/, '/applications/create/:leadId')
    .replace(/\/applications\/[^/]+\/residence-verification/, '/applications/:appId/residence-verification')
    .replace(/\/applications\/[^/]+/, '/applications/:appId')
    .replace(/\/groups\/[^/]+/, '/groups/:id')
    .replace(/\/tasks\/[^/]+/, '/tasks/:taskId');
}

export function initRUM(): void {
  const appMonitorId = process.env.REACT_APP_RUM_APP_MONITOR_ID;
  const identityPoolId = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
  const guestRoleArn = process.env.REACT_APP_RUM_GUEST_ROLE_ARN;
  const region = process.env.REACT_APP_REGION || 'ap-south-1';

  // Skip if config is missing (local dev without RUM setup)
  if (!appMonitorId || !identityPoolId || !guestRoleArn) {
    return;
  }

  // Skip on localhost — RUM App Monitor domain won't match
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return;
  }

  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1, // 100% of sessions
      identityPoolId,
      guestRoleArn,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
      pageIdFormat: 'PATH',
      disableAutoPageView: true, // We record page views manually with normalized paths via RumPageTracker
    };

    rumClient = new AwsRum(appMonitorId, '1.0.0', region, config);
  } catch {
    // RUM init failure should never block the app
  }
}

/** Get the RUM client instance (for custom events if needed) */
export function getRumClient(): AwsRum | null {
  return rumClient;
}

/**
 * Record a custom event in CloudWatch RUM.
 * Silently no-ops if RUM is not initialized.
 */
export function recordRumEvent(eventType: string, data: Record<string, unknown>): void {
  try {
    rumClient?.recordEvent(eventType, data);
  } catch {
    // Never block the app for telemetry failures
  }
}

/**
 * Record an API error event with full details for debugging.
 * Call this from the API client's error interceptor.
 */
export function recordApiError(details: {
  url?: string;
  method?: string;
  status?: number;
  message?: string;
  userId?: string;
}): void {
  recordRumEvent('api_error', {
    url: details.url || 'unknown',
    method: (details.method || 'unknown').toUpperCase(),
    status: details.status || 0,
    message: details.message || 'Unknown error',
    userId: details.userId || 'anonymous',
    timestamp: new Date().toISOString(),
  });
}
